/**
 * Export Service - PDF/Excel/CSV generation for dashboard KPI data
 *
 * v2.95 - 2026-02-07 - Initial implementation
 * - exportToPDF: jspdf + jspdf-autotable for PDF generation
 * - exportToExcel: xlsx (SheetJS) for multi-sheet Excel workbooks
 * - exportToCSV: Simple CSV export of KPI values
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from './api';

// v2.95 - Fetch export data from backend (uses authenticated axios instance)
const fetchExportData = async () => {
  const response = await api.get('/dashboard/export-data');
  return response.data;
};

// v2.95 - Format KPI value based on format type
const formatValue = (value: number | null | undefined, format: string, formatOptions?: any): string => {
  if (value === null || value === undefined) return 'N/A';

  if (format === 'currency') {
    const currency = formatOptions?.currency || 'USD';
    const decimals = formatOptions?.decimals ?? 0;
    const abbreviate = formatOptions?.abbreviate;

    if (abbreviate && Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (abbreviate && Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  if (format === 'percentage') {
    return `${value.toFixed(1)}%`;
  }

  // Default: number
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

// v2.95 - Format change direction with arrow
const formatChange = (changePercent: number | null | undefined, changeDirection: string | undefined): string => {
  if (changePercent === null || changePercent === undefined) return '-';
  const arrow = changeDirection === 'up' ? '\u2191' : changeDirection === 'down' ? '\u2193' : '\u2192';
  return `${arrow} ${Math.abs(changePercent).toFixed(1)}%`;
};

/**
 * Export dashboard KPIs to PDF
 * v2.95 - Uses jspdf + jspdf-autotable
 */
export const exportToPDF = async (dashboardName?: string): Promise<void> => {
  const data = await fetchExportData();
  const { cards, kpis, tenantName, computedAt, exportedAt } = data;
  const title = dashboardName || `${tenantName || 'MyLojic'} Dashboard`;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header
  doc.setFontSize(18);
  doc.setTextColor(0, 51, 102); // Navy
  doc.text(title, 14, 20);

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date(exportedAt).toLocaleString()}`, 14, 28);
  if (computedAt) {
    doc.text(`Data as of: ${new Date(computedAt).toLocaleString()}`, 14, 33);
  }

  // Divider line
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(0.5);
  doc.line(14, 36, pageWidth - 14, 36);

  // KPI Summary Table
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('KPI Summary', 14, 44);

  const tableData = cards.map((card: any) => {
    const kpi = kpis[card.cardId?.replace('card-', '')] || kpis[card.cardId] || {};
    return [
      card.title || 'Untitled',
      formatValue(kpi.currentValue, card.format, card.formatOptions),
      formatValue(kpi.previousValue, card.format, card.formatOptions),
      formatChange(kpi.changePercent, kpi.changeDirection),
      card.category || 'general',
    ];
  });

  autoTable(doc, {
    startY: 48,
    head: [['KPI', 'Current Value', 'Previous Value', 'Change', 'Category']],
    body: tableData,
    headStyles: {
      fillColor: [0, 51, 102],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [50, 50, 50],
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { left: 14, right: 14 },
    theme: 'grid',
  });

  // Sparkline note (text-based since SVG capture is unreliable with html2canvas)
  const finalY = (doc as any).lastAutoTable?.finalY || 120;
  if (finalY + 30 < doc.internal.pageSize.getHeight()) {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(
      'Note: Sparkline charts are available in the online dashboard.',
      14,
      finalY + 12
    );
  }

  // Footer
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Powered by MyLojic', 14, pageHeight - 10);
  doc.text('Page 1 of 1', pageWidth - 35, pageHeight - 10);

  doc.save(`${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

/**
 * Export dashboard KPIs to Excel (.xlsx)
 * v2.95 - Uses xlsx (SheetJS) with multiple sheets
 */
export const exportToExcel = async (dashboardName?: string): Promise<void> => {
  const data = await fetchExportData();
  const { cards, kpis, tenantName, computedAt, exportedAt } = data;
  const title = dashboardName || `${tenantName || 'MyLojic'} Dashboard`;

  const wb = XLSX.utils.book_new();

  // Sheet 1: KPI Summary
  const summaryData = cards.map((card: any) => {
    const kpi = kpis[card.cardId?.replace('card-', '')] || kpis[card.cardId] || {};
    return {
      'KPI Title': card.title || 'Untitled',
      'Current Value': kpi.currentValue ?? '',
      'Previous Value': kpi.previousValue ?? '',
      'Change %': kpi.changePercent != null ? `${kpi.changePercent.toFixed(1)}%` : '',
      'Trend': kpi.changeDirection || '',
      'Category': card.category || 'general',
      'Format': card.format || 'number',
    };
  });

  const ws1 = XLSX.utils.json_to_sheet(summaryData);
  ws1['!cols'] = [
    { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(wb, ws1, 'KPI Summary');

  // Sheet 2: KPI Details (per-card with sparkline data as columns)
  const detailData = cards.map((card: any) => {
    const kpi = kpis[card.cardId?.replace('card-', '')] || kpis[card.cardId] || {};
    const sparkline = kpi.sparkline || [];
    const periods = kpi.sparklinePeriods || [];

    const row: any = {
      'Card ID': card.cardId || '',
      'Title': card.title || 'Untitled',
      'Gold Table': card.goldTable || '',
      'Value Column': card.valueColumn || '',
      'Current Value': kpi.currentValue ?? '',
      'As Of Date': kpi.asOfDate || '',
    };

    // Add sparkline data as columns
    for (let i = 0; i < Math.min(sparkline.length, 12); i++) {
      row[periods[i] || `Period ${i + 1}`] = sparkline[i] ?? '';
    }

    return row;
  });

  const ws2 = XLSX.utils.json_to_sheet(detailData);
  XLSX.utils.book_append_sheet(wb, ws2, 'KPI Details');

  // Sheet 3: Metadata
  const metaData = [
    { Property: 'Dashboard', Value: title },
    { Property: 'Tenant', Value: tenantName || '' },
    { Property: 'Exported At', Value: new Date(exportedAt).toLocaleString() },
    { Property: 'Data Computed At', Value: computedAt ? new Date(computedAt).toLocaleString() : 'N/A' },
    { Property: 'Total KPIs', Value: String(cards.length) },
    { Property: 'Platform', Value: 'MyLojic' },
  ];

  const ws3 = XLSX.utils.json_to_sheet(metaData);
  ws3['!cols'] = [{ wch: 20 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, ws3, 'Metadata');

  XLSX.writeFile(wb, `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

/**
 * Export dashboard KPIs to CSV
 * v2.95 - Simple CSV export of KPI values
 */
export const exportToCSV = async (dashboardName?: string): Promise<void> => {
  const data = await fetchExportData();
  const { cards, kpis, tenantName } = data;
  const title = dashboardName || `${tenantName || 'MyLojic'} Dashboard`;

  const headers = ['KPI Title', 'Current Value', 'Previous Value', 'Change %', 'Trend', 'Category'];
  const rows = cards.map((card: any) => {
    const kpi = kpis[card.cardId?.replace('card-', '')] || kpis[card.cardId] || {};
    return [
      `"${(card.title || 'Untitled').replace(/"/g, '""')}"`,
      kpi.currentValue ?? '',
      kpi.previousValue ?? '',
      kpi.changePercent != null ? `${kpi.changePercent.toFixed(1)}%` : '',
      kpi.changeDirection || '',
      card.category || 'general',
    ].join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
